import React from "react";
import { Socket } from "socket.io-client";
import ILoadUserDTO from "../../../../models/DTO/ILoadUserDTO";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import FileUploadResourceTypeEnum from "../../../../models/FileUploadResourceTypeEnum";
import IAttachment from "../../../../models/IAttachment";
import ISupplierModel from "../../../../models/ISupplierModel";
import IUserUI from "../../../../models/IUserUI";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwS3Uploader from "../../../shared/OwS3Uploader/OwS3Uploader";
import "./LogoTile.scss";

/** Which logo on the business this tile uploads to. */
export type LogoTileKind = "dark" | "light" | "doc";

type Props = {
    kind: LogoTileKind;
    socket: Socket;
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    /** The logo the business has now, if any. */
    attachment?: IAttachment | null;
};

const SETUP: Record<LogoTileKind, { titleKey: string; helpKey: string; previewKey: string; iconClass: string; surface: "dark" | "light" }> =
    {
        dark: {
            titleKey: "dark_mode_logo",
            helpKey: "help_text_dark_mode_logo",
            previewKey: "logo_preview_dark",
            iconClass: "fad fa-moon",
            surface: "dark",
        },
        light: {
            titleKey: "light_mode_logo",
            helpKey: "help_text_light_mode_logo",
            previewKey: "logo_preview_light",
            iconClass: "fad fa-sun",
            surface: "light",
        },
        doc: {
            titleKey: "doc_logo",
            helpKey: "help_text_doc_logo_tile",
            previewKey: "logo_preview_doc",
            iconClass: "fad fa-file-lines",
            surface: "light",
        },
    };

/**
 * One logo: a preview on the background it will actually sit on, the file it is now, and a
 * one-line drop zone under it. Logos upload on drop, so the tile refreshes the business
 * once the server has recorded the file.
 */
export const LogoTile = (props: Props) => {
    const setup = SETUP[props.kind];
    const signedUrl = props.attachment?.signedUrl;

    const refreshSupplier = () => {
        const payload: ILoadUserDTO = {
            userGuid: props.userModel.guid,
            userToken: props.userModel.token,
            supplierGuid: props.supplierModel.guid,
        };

        props.socket.emit(EndpointsEnum.AppLoadUser, payload);
    };

    return (
        <div className={`LogoTile ${props.kind}`} data-testid={`logo-tile-${props.kind}`}>
            {/* The whole tile is the drop target; the zone line under the preview opens the picker. */}
            <OwS3Uploader
                socket={props.socket}
                userModel={props.userModel}
                supplierModel={props.supplierModel}
                isReadOnly={false}
                resourceType={props.kind === "doc" ? FileUploadResourceTypeEnum.docLogo : FileUploadResourceTypeEnum.logo}
                isWhiteLabelLogo={props.kind === "dark"}
                isWhiteLabelLightLogo={props.kind === "light"}
                isDocLogo={props.kind === "doc"}
                imagesOnly={true}
                compact={true}
                dropLabelKey={signedUrl ? "drop_image_to_replace" : "drop_image_or_browse"}
                fileTypeInvalidKey={"logo_file_type_invalid"}
                onUploaded={refreshSupplier}
                testId={`logo-tile-${props.kind}-upload`}
                zoneAside={
                    <span className={"LogoTile-meta"}>
                        <OwLabel title={"logo_file_hint"} noBottomMargin={true} />
                    </span>
                }>
                <div className={"LogoTile-head"}>
                    <span className={`LogoTile-icon ${setup.surface}`}>
                        <i className={setup.iconClass} aria-hidden={true} />
                    </span>
                    <div className={"LogoTile-text"}>
                        <div className={"LogoTile-title"}>
                            <OwLabel title={setup.titleKey} noBottomMargin={true} />
                        </div>
                        <div className={"LogoTile-help"}>
                            <OwLabel title={setup.helpKey} noBottomMargin={true} />
                        </div>
                    </div>
                    {signedUrl ? (
                        <span className={"SettingsChip on LogoTile-chip"} data-testid={`logo-tile-${props.kind}-file`}>
                            <i className={"fad fa-check"} aria-hidden={true} />
                            {props.attachment?.name || <OwLabel title={"logo_uploaded"} noBottomMargin={true} />}
                        </span>
                    ) : (
                        <span className={"SettingsChip LogoTile-chip"} data-testid={`logo-tile-${props.kind}-empty`}>
                            <OwLabel title={"no_logo_yet"} noBottomMargin={true} />
                        </span>
                    )}
                </div>

                <div className={`LogoTile-preview ${setup.surface}`}>
                    {signedUrl ? (
                        <img src={signedUrl} alt={""} data-testid={`logo-tile-${props.kind}-image`} />
                    ) : (
                        <div className={"LogoTile-ghost"}>
                            <i className={"fad fa-image"} aria-hidden={true} />
                            <OwLabel title={setup.previewKey} noBottomMargin={true} />
                        </div>
                    )}
                </div>
            </OwS3Uploader>
        </div>
    );
};
